// Shared typed-intake contract (typed help/intake framework, commit 1).
//
// One source of truth for the intake vocabularies, the intake status graph, and
// the client-safe field bounds, consumed by:
//   - the API service/route guards (feedbackIntakes.service.js, routes)
//   - migration 025's column widths and CHECK constraints
//   - the later web verticals, which must not restate any of these values
//
// This module is pure data plus pure functions: no React, no API, no Node-only
// imports. Statuses and types are matched EXACTLY as persisted — no trimming and
// no case folding — because a value that differs from the persisted vocabulary is
// itself an unknown state the caller must refuse rather than repair.
//
// The intake aggregate owns authoring/conversation state. Governed queue state
// stays on feedback_records; these two vocabularies must never be merged.

export const FEEDBACK_INTAKE_TYPES = Object.freeze([
  'support_ticket',
  'feature_service_request',
  'general_question',
]);

export const FEEDBACK_INTAKE_STATUSES = Object.freeze([
  'draft',
  'collecting',
  'ready_for_review',
  'submitted',
  'deflected',
  'cancelled',
]);

// Explicit edges only. `deflected -> submitted` is the "Ask the team anyway"
// path: a reporter who received a satisfactory answer can still escalate, and
// that escalation must produce a governed record like any other submission.
export const FEEDBACK_INTAKE_STATUS_GRAPH = Object.freeze({
  draft: Object.freeze(['collecting', 'ready_for_review', 'cancelled']),
  collecting: Object.freeze(['ready_for_review', 'deflected', 'cancelled']),
  ready_for_review: Object.freeze(['submitted', 'collecting', 'cancelled']),
  deflected: Object.freeze(['submitted']),
  submitted: Object.freeze([]),
  cancelled: Object.freeze([]),
});

// Derived from the graph rather than hand-listed, so a future edge addition
// cannot leave a status marked terminal while it still has an exit.
export const FEEDBACK_INTAKE_TERMINAL_STATUSES = Object.freeze(
  FEEDBACK_INTAKE_STATUSES.filter((status) => FEEDBACK_INTAKE_STATUS_GRAPH[status].length === 0),
);

// `user` is the only actor a client-facing endpoint may write. `assistant` and
// `system` are reserved for server-originated flows (AI answers, lifecycle
// notes) and are rejected on the public message endpoint.
export const FEEDBACK_INTAKE_MESSAGE_ACTORS = Object.freeze(['user', 'assistant', 'system']);

// feedback_records.entry_type: which authoring surface produced the governed
// record. `screen_feedback` is the pre-existing screenshot-package path and is
// what migration 025 backfills onto every existing row.
export const FEEDBACK_ENTRY_TYPES = Object.freeze(['screen_feedback', ...FEEDBACK_INTAKE_TYPES]);

export const FEEDBACK_SUBMISSION_CHANNELS = Object.freeze(['screen_capture', 'typed_intake']);

export const FEEDBACK_DEFLECTION_OUTCOMES = Object.freeze([
  'deflected',
  'handoff_submitted',
  'asked_team_anyway',
  'not_applicable',
]);

// Column-width contracts. Lengths are measured in UTF-16 code units via
// String.length — identical semantics in the current Node and browser runtimes,
// and the same unit NVARCHAR counts. Exactly the limit is accepted; one more is
// not.
export const FEEDBACK_INTAKE_TITLE_MAX_LENGTH = 400;
export const FEEDBACK_INTAKE_SOURCE_ROUTE_MAX_LENGTH = 256;
export const FEEDBACK_INTAKE_CURRENT_STEP_MAX_LENGTH = 64;
export const FEEDBACK_INTAKE_IDENTIFIER_MAX_LENGTH = 128;
// Deliberately the same 4000-character budget as FEEDBACK_COMMENT_MAX_LENGTH in
// feedbackLifecycle.js: reporters should not have to learn two different
// "how much can I type" rules across the feedback surfaces.
export const FEEDBACK_INTAKE_MESSAGE_MAX_LENGTH = 4000;
export const FEEDBACK_INTAKE_DETAIL_MAX_LENGTH = 4000;

// Allowlisted, versioned intake detail keys. Anything else is rejected server
// side rather than stored: an unknown key is either a client bug or an attempt
// to smuggle untrusted context into the governed record.
export const FEEDBACK_INTAKE_DETAIL_SCHEMA_VERSION = 1;
export const FEEDBACK_INTAKE_DETAIL_FIELDS = Object.freeze([
  'description',
  'expectedBehavior',
  'actualBehavior',
  'reproductionSteps',
  'impact',
  'businessNeed',
  'question',
]);

// Provenance fields on a governed feedback_records row. These describe WHERE a
// record came from, so they are server-owned and immutable for the record's
// whole life: reviewer triage, conversion, request-more-info, and replay must
// all carry them through unchanged. Every governed-record codec (the screenshot
// normalizer, the typed-intake materializer, the governance update path, and the
// SQL projections) references THIS list so none of them can silently drop a
// field the others still write.
export const FEEDBACK_RECORD_PROVENANCE_FIELDS = Object.freeze([
  'entryType',
  'sourceIntakeId',
  'submissionChannel',
  'deflectionOutcome',
  'intakeSnapshot',
]);

// Provenance for a record produced by the pre-existing screenshot/direct paths.
// Identical to what migration 025 backfills onto historical rows, so a record
// written after 025 is indistinguishable from one written before it.
export const FEEDBACK_SCREEN_CAPTURE_PROVENANCE = Object.freeze({
  entryType: 'screen_feedback',
  sourceIntakeId: null,
  submissionChannel: 'screen_capture',
  deflectionOutcome: 'not_applicable',
  intakeSnapshot: null,
});

// -------------------------------------------------------------------------
// Presentation vocabulary (commit 6)
// -------------------------------------------------------------------------

// ONE source of truth for the reporter-facing and reviewer-facing name of each
// authoring surface. My Feedback and the reviewer queue are now two views over
// the same rows, so a type named "Support ticket" on one page and "Support" on
// the other would make a reporter and a reviewer describe the same record
// differently. `screen_feedback` is included because the queue filters over
// ENTRY types, which include the pre-existing screenshot path.
export const FEEDBACK_ENTRY_TYPE_LABELS = Object.freeze({
  screen_feedback: 'Screen feedback',
  support_ticket: 'Support ticket',
  feature_service_request: 'Feature or service request',
  general_question: 'Question',
});

// The plural forms used by filter controls and section headings.
export const FEEDBACK_ENTRY_TYPE_PLURAL_LABELS = Object.freeze({
  screen_feedback: 'Screen feedback',
  support_ticket: 'Support tickets',
  feature_service_request: 'Feature requests',
  general_question: 'Questions',
});

// An unrecognized value is echoed back RAW rather than guessed at or hidden: a
// record whose entry type PRIME does not know about is a data problem a reviewer
// needs to see, and inventing a friendly name for it would conceal exactly that.
// Own-property lookups only (commit-6 delta re-audit): a value such as
// '__proto__', 'constructor', or 'toString' must be echoed as an unknown string,
// not resolve an inherited property whose value is an object or function that
// breaks the rendering surface consuming the label.
export function feedbackEntryTypeLabel(value) {
  if (Object.hasOwn(FEEDBACK_ENTRY_TYPE_LABELS, value)) return FEEDBACK_ENTRY_TYPE_LABELS[value];
  return String(value ?? '') || 'Unclassified';
}

export function feedbackEntryTypePluralLabel(value) {
  if (Object.hasOwn(FEEDBACK_ENTRY_TYPE_PLURAL_LABELS, value)) {
    return FEEDBACK_ENTRY_TYPE_PLURAL_LABELS[value];
  }
  return feedbackEntryTypeLabel(value);
}

// HOW a governed record was authored, in words. `screen_capture` and
// `typed_intake` are storage values, and a reviewer detail panel that printed
// them raw would be asking the reviewer to learn the column vocabulary. Same
// chokepoint rule as the entry-type labels: one map, so the queue and any future
// export describe the channel identically.
export const FEEDBACK_SUBMISSION_CHANNEL_LABELS = Object.freeze({
  screen_capture: 'Screen capture',
  typed_intake: 'Typed request',
});

// An unrecognized channel is echoed back RAW for the same reason an unrecognized
// entry type is: it is a data problem a reviewer needs to see, not something to
// paper over with a friendly name.
export function feedbackSubmissionChannelLabel(value) {
  if (Object.hasOwn(FEEDBACK_SUBMISSION_CHANNEL_LABELS, value)) {
    return FEEDBACK_SUBMISSION_CHANNEL_LABELS[value];
  }
  return String(value ?? '') || null;
}

// -------------------------------------------------------------------------
// My Feedback state classification (commit 6)
// -------------------------------------------------------------------------

// The four states the unified My Feedback filter offers. They are DERIVED, never
// stored: `drafts`/`submitted`/`deflected` come from the intake aggregate, and
// `resolved` additionally needs the linked governed record, because an intake has
// no resolved state of its own — the product team resolves the RECORD.
//
// Deliberately the same vocabulary as the pre-existing screenshot-package
// lifecycle (draft / submitted / resolved) plus the one state packages cannot
// have, so the filter means the same thing for both sources.
export const MY_FEEDBACK_STATES = Object.freeze(['drafts', 'submitted', 'resolved', 'deflected']);

// Governed record statuses that close the work. Same set the package lifecycle
// derivation uses, so a resolved package and a resolved typed intake agree.
export const MY_FEEDBACK_TERMINAL_RECORD_STATUSES = Object.freeze([
  'resolved',
  'rejected',
  'duplicate',
]);

export function isMyFeedbackState(value) {
  return MY_FEEDBACK_STATES.includes(value);
}

/**
 * Which My Feedback state a typed intake belongs to.
 *
 * `recordStatus` is the status of the linked governed record when the intake has
 * been materialized; without it a submitted intake stays `submitted` rather than
 * being optimistically reported as resolved. `cancelled` intakes belong to no
 * state and return null, so they are filtered out of every state view instead of
 * silently landing in Drafts.
 */
export function intakeMyFeedbackState(intake) {
  const status = intake?.status ?? null;
  if (status === 'deflected') return 'deflected';
  if (status === 'cancelled') return null;
  if (status === 'submitted') {
    return MY_FEEDBACK_TERMINAL_RECORD_STATUSES.includes(
      intake?.governedRecord?.status ?? intake?.recordStatus ?? null,
    )
      ? 'resolved'
      : 'submitted';
  }
  if (isFeedbackIntakeStatus(status)) return 'drafts';
  return null;
}

/** Does this intake pass the unified type + state filter pair? */
export function intakeMatchesMyFeedbackFilters(intake, { type = '', state = '' } = {}) {
  if (type && intake?.intakeType !== type) return false;
  if (!state) return true;
  return intakeMyFeedbackState(intake) === state;
}

export function isFeedbackIntakeType(value) {
  return FEEDBACK_INTAKE_TYPES.includes(value);
}

export function isFeedbackIntakeStatus(value) {
  return FEEDBACK_INTAKE_STATUSES.includes(value);
}

export function isFeedbackIntakeMessageActor(value) {
  return FEEDBACK_INTAKE_MESSAGE_ACTORS.includes(value);
}

export function isFeedbackEntryType(value) {
  return FEEDBACK_ENTRY_TYPES.includes(value);
}

export function isFeedbackSubmissionChannel(value) {
  return FEEDBACK_SUBMISSION_CHANNELS.includes(value);
}

export function isFeedbackDeflectionOutcome(value) {
  return FEEDBACK_DEFLECTION_OUTCOMES.includes(value);
}

// Governed entry type for a typed intake. The two vocabularies share their
// member names on purpose (one intake type produces one entry type), but they
// are separate contracts: entry types also cover `screen_feedback`, which is
// not an intake type.
export function entryTypeForIntakeType(intakeType) {
  return isFeedbackIntakeType(intakeType) ? intakeType : null;
}

export function nextIntakeStatuses(status) {
  const targets = FEEDBACK_INTAKE_STATUS_GRAPH[status];
  return Array.isArray(targets) ? targets : [];
}

// Explicit-edge semantics: a self-transition is FALSE. Callers that want to
// treat "no change" as acceptable must special-case it, so that a stale
// `submitted -> submitted` write can never slip through as a valid transition.
export function canTransitionIntakeStatus(from, to) {
  if (!isFeedbackIntakeStatus(from) || !isFeedbackIntakeStatus(to)) return false;
  return nextIntakeStatuses(from).includes(to);
}

export function isTerminalIntakeStatus(status) {
  return FEEDBACK_INTAKE_TERMINAL_STATUSES.includes(status);
}
